import React, { ReactNode } from 'react';
import MuiDialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { SxProps, Theme } from '@mui/material';
import { DIALOG_TYPE } from '../../../../constants/DialogTypes';
import { DialogActionButtons } from './DialogActionButtons';

export interface IDialog {
  isOpen: boolean;
  onClose: () => void;
  handleApply: () => void;
  title: string;
  children: ReactNode;
  dialogType?: DIALOG_TYPE;
  justify?: 'start' | 'center' | 'end';
  confirmButtonLable: string;
  cancelButtonLable?: string;
  disabled?: boolean;
  maxWidth?: string; // e.g. '700px', '900px'
  noScroll?: boolean;
  closeOnOutsideClick?: boolean;
  sx?: SxProps<Theme>;
}

export const Dialog = ({
  isOpen,
  onClose,
  title,
  children,
  dialogType = DIALOG_TYPE.INFO,
  handleApply,
  justify = 'end',
  cancelButtonLable,
  confirmButtonLable,
  disabled,
  maxWidth = '700px',
  noScroll = false,
  closeOnOutsideClick = true,
  sx,
}: IDialog) => {
  return (
    <MuiDialog
      open={Boolean(isOpen)}
      onClose={(event, reason) => {
        if (!closeOnOutsideClick && reason === 'backdropClick') {
          return;
        }
        onClose();
      }}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: maxWidth || '700px',
          width: '95%',
          borderRadius: '20px',
          bgcolor: 'background.paper',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          m: 'auto',
          ...sx,
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(15, 23, 42, 0.45)',
        },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          m: 0,
          px: { xs: 2.5, sm: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1rem', sm: '1.2rem' },
            color: 'text.primary',
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </Typography>

        <IconButton
          aria-label="Close dialog"
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            bgcolor: 'grey.100',
            borderRadius: '10px',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'error.50',
              color: 'error.main',
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* Dialog Body */}
      <DialogContent
        sx={{
          p: { xs: 2.5, sm: 3 },
          maxHeight: noScroll ? undefined : '65vh',
          overflowY: noScroll ? 'visible' : 'auto',
        }}
      >
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.6, mb: 3 }}>
          {children}
        </Box>

        {/* Action Buttons */}
        <DialogActionButtons
          onClose={onClose}
          handleApply={handleApply}
          dialogType={dialogType}
          justify={justify}
          actionButton={confirmButtonLable}
          cancelButton={cancelButtonLable}
          disabled={disabled}
        />
      </DialogContent>
    </MuiDialog>
  );
};

export default Dialog;
